'use client';

import type { ComponentType } from 'react';
import { CalendarRange, ChartColumnStacked, TrendingUp } from 'lucide-react';
import { EventData } from './types';
import { formatCompactValue } from '@/lib/utils/number';
import dayjs from 'dayjs';
import { useTranslation } from '@/lib/i18n';

interface MarketOverviewProps {
  eventData: EventData;
}

interface OverviewItem {
  title: string;
  value: string;
  subValue?: string;
  icon: ComponentType<{ className?: string; size?: number; strokeWidth?: number }>;
}

export default function MarketOverview({ eventData }: MarketOverviewProps) {
  const { t } = useTranslation();
  const { event } = eventData;
  const items: OverviewItem[] = [
    {
      title: t('Total Volume'),
      value: formatCompactValue(parseMarketNumber(event.total_volume), '$'),
      subValue: event.volume_24hr
        ? `($${formatCompactValue(parseMarketNumber(event.volume_24hr))} ${t('24h')})`
        : undefined,
      icon: ChartColumnStacked,
    },
    {
      title: t('Open Interest'),
      value: formatCompactValue(parseMarketNumber(event.open_interest), '$'),
      icon: TrendingUp,
    },
    // {
    //   title: t('Ends'),
    //   value: dayjs(event.end_date || event.closed_time).format('MMM DD, YYYY'),
    //   icon: CalendarRange,
    // },
  ];

  return (
    <section className="@container">
      <h2 className="py-5 text-lg font-medium">{t('Overview')}</h2>
      <div className="grid grid-cols-2 gap-4">
        {items.map((item) => (
          <OverviewCard key={item.title} item={item} />
        ))}
      </div>
    </section>
  );
}

function OverviewCard({ item }: { item: OverviewItem }) {
  // const Icon = item.icon;

  return (
    <div className="relative rounded-xl border border-white/15 bg-black px-5 py-3">
      <div className="text-sm text-[#757575]">{item.title}</div>
      <div className="flex min-w-0 items-baseline gap-3 pr-15.5">
        <span className="whitespace-nowrap text-lg font-medium">{item.value}</span>
        {item.subValue && (
          <span className="whitespace-nowrap text-xs text-[#B7B9BC]">{item.subValue}</span>
        )}
      </div>
      {/* <Icon className="absolute @max-2xl:hidden top-1/2 -translate-y-1/2 right-5 text-[#777777]" /> */}
    </div>
  );
}

function parseMarketNumber(value: string | number | null | undefined) {
  if (value === null || value === undefined) return undefined;

  const parsed = typeof value === 'number' ? value : Number(value.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : undefined;
}
